import type RootStore from "stores/RootStore";
import { throwIfFailed } from "shared/dialogSubmit";
import type { DialogType } from "types/DialogType";
import ticketsRoute from "./tickets.Route";
import type TicketDeskStore from "./TicketDeskStore";
import type ClientTicketsStore from "./ClientTicketsStore";

/** The desk's dialog name; Client 360 registers the same dialog under it. */
export const NEW_TICKET_DIALOG = "NewTicketDialog";

/**
 * The ticket desk's New ticket dialog, registered on Client 360 as well so the
 * Tickets tab can file a ticket about the open client without leaving the page
 * (dialogs are per-route). Title, body, size and validation are the desk's own
 * — taken from the desk's registration so the two cannot drift; the draft is
 * the desk store's, preset to the client by `ClientTicketsStore.startNewTicket`.
 * Only the submit differs: a refused filing THROWS (the dialog stays open with
 * the reason), and a filed one re-reads this client's tickets so the tab
 * shows it.
 */
const deskDialog = ticketsRoute[0]?.dialogs?.find((d) => d.name === NEW_TICKET_DIALOG);

export const clientNewTicketDialog: DialogType = {
  ...deskDialog,
  name: NEW_TICKET_DIALOG,
  title: deskDialog?.title ?? "New ticket",
  component: deskDialog?.component ?? (() => import("./dialogs/NewTicketDialog.Body")),
  submitButtonText: deskDialog?.submitButtonText ?? "Create ticket",
  onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
    const desk = rootStore.ticketDeskStore as TicketDeskStore | undefined;
    // `follow: false`: file the ticket and nothing else. The desk's default is
    // to switch to "I asked", re-read its list and open the ticket in its
    // drawer — a desk the person is not looking at, changed behind their back.
    const created = desk ? await desk.submitNewTicket({ follow: false }) : null;
    // The same rule as the desk's own dialog (tickets.Route.ts). A refused
    // filing already REJECTED inside submitNewTicket; a null with no throw is
    // nothing sent (a desk that is not wired) — or a re-entered press while one
    // is in flight, which is not a refusal and must not show as one.
    // `wiringError` is the reason for the first; `actionError` is the DRAWER's
    // field and says nothing about a filing.
    if (!created && !desk?.submitting) {
      throwIfFailed(false, desk?.wiringError, "The ticket could not be filed.");
    }
    if (!created) return;
    await (rootStore.clientTicketsTabStore as unknown as ClientTicketsStore | undefined)?.load();
  },
};
